import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Sparkles, ExternalLink, Loader2, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { isoUtcToMadridLocal, madridLocalToIsoUtc, previewMadrid } from '../../lib/madridTime';

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-medium text-[hsl(var(--text-secondary))]">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-[hsl(var(--text-tertiary))]">{hint}</p>}
  </div>
);

const eur = (cents, currency='eur') =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: 2 }).format((cents || 0) / 100);

export default function AdminPaidWorkshopEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/admin/paid-workshops/${slug}`);
      setData(r.data);
      setForm(r.data.workshop || {});
    } catch {
      toast.error('No se pudo cargar el taller');
      navigate('/admin/paid-workshops');
    }
  };
  useEffect(() => { load(); }, [slug]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Only send fields the API accepts
      delete payload.active_tier;
      delete payload.id;
      delete payload.slug;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.landing_variant;
      await api.put(`/admin/paid-workshops/${slug}`, payload);
      toast.success('Guardado');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const activateTier = async (tierName) => {
    try {
      await api.post(`/admin/paid-workshops/${slug}/activate-tier/${encodeURIComponent(tierName)}`);
      toast.success(`Lote activo: ${tierName}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const addTier = () => {
    const tiers = [...(form.price_tiers || [])];
    tiers.push({ name: `LOTE ${tiers.length}`, amount_cents: 1500, currency: 'eur', stripe_lookup_key: '', seats_total: 0, seats_sold: 0, is_active: false });
    update('price_tiers', tiers);
  };
  const removeTier = (i) => {
    const tiers = [...(form.price_tiers || [])];
    tiers.splice(i, 1);
    update('price_tiers', tiers);
  };
  const updateTier = (i, k, v) => {
    const tiers = [...(form.price_tiers || [])];
    tiers[i] = { ...tiers[i], [k]: v };
    update('price_tiers', tiers);
  };

  if (!data) {
    return <div className="pt-20 min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#c9a227]" /></div>;
  }

  const exportCSV = () => {
    if (!data.enrollments?.length) return;
    const rows = [['Email','Lote','Grabación','Session ID','Inscrita el']];
    data.enrollments.forEach(e => rows.push([e.email||'', e.tier_name||'', e.with_recording?'sí':'no', e.stripe_session_id||'', e.enrolled_at||'']));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], {type:'text/csv;charset=utf-8'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${slug}-inscritas-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="pt-20 min-h-screen bg-[#FDFCFA] pb-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-10">
        <div className="mb-8">
          <Link to="/admin/paid-workshops" className="text-xs text-[hsl(var(--text-tertiary))] hover:text-[#c9a227] inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Volver a talleres de pago
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h1 className="text-3xl font-heading text-[hsl(var(--text-primary))] flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#c9a227]" /> {data.workshop.title}
            </h1>
            <Link to={`/masterclass/${slug}`} target="_blank" className="text-sm text-[#c9a227] hover:underline inline-flex items-center gap-1">
              <ExternalLink className="w-4 h-4" /> Landing pública
            </Link>
          </div>
          <p className="text-xs text-[hsl(var(--text-tertiary))] font-mono mt-1">/{slug}</p>
        </div>

        {/* Estado */}
        <div className="card-soft mb-6 space-y-4">
          <h2 className="text-lg font-heading">Estado</h2>
          <Field label="Estado">
            <select value={form.status || 'draft'} onChange={e => update('status', e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm bg-white" data-testid="pw-admin-status">
              <option value="draft">Borrador (no visible)</option>
              <option value="published">Publicado (visible)</option>
              <option value="archived">Archivado</option>
            </select>
          </Field>
        </div>

        {/* Contenido básico */}
        <div className="card-soft mb-6 space-y-4">
          <h2 className="text-lg font-heading">Contenido principal</h2>
          <Field label="Título"><input value={form.title || ''} onChange={e => update('title', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" data-testid="pw-admin-title" /></Field>
          <Field label="Eyebrow (banda superior)"><input value={form.eyebrow || ''} onChange={e => update('eyebrow', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" /></Field>
          <Field label="Hero — titular"><textarea rows={3} value={form.hero_headline || ''} onChange={e => update('hero_headline', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" /></Field>
          <Field label="Hero — cuerpo"><textarea rows={4} value={form.hero_body || ''} onChange={e => update('hero_body', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Fecha y hora inicio (hora Madrid)" hint={form.date_iso ? previewMadrid(form.date_iso) : ''}>
              <input type="datetime-local" value={isoUtcToMadridLocal(form.date_iso)} onChange={e => update('date_iso', madridLocalToIsoUtc(e.target.value))} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" data-testid="pw-admin-date" />
            </Field>
            <Field label="Hora fin (hora Madrid)" hint={form.end_iso ? previewMadrid(form.end_iso) : ''}>
              <input type="datetime-local" value={isoUtcToMadridLocal(form.end_iso)} onChange={e => update('end_iso', madridLocalToIsoUtc(e.target.value))} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" />
            </Field>
          </div>
          <Field label="Ubicación"><input value={form.location || ''} onChange={e => update('location', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" /></Field>
          <Field label="Imagen de portada (URL)"><input value={form.cover_image_url || ''} onChange={e => update('cover_image_url', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm font-mono border-[hsl(var(--border))]" /></Field>
          <Field label="Imagen del autor (URL)"><input value={form.author_image_url || ''} onChange={e => update('author_image_url', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm font-mono border-[hsl(var(--border))]" /></Field>
        </div>

        {/* Lotes de precio */}
        <div className="card-soft mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-heading">Lotes de precio</h2>
              <p className="text-[11px] text-[hsl(var(--text-tertiary))]">El lote marcado como "activo" es el que se muestra en la landing.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={addTier}><Plus className="w-4 h-4 mr-1"/>Añadir lote</Button>
          </div>
          <div className="space-y-3">
            {(form.price_tiers || []).map((t, i) => (
              <div key={i} className={`p-4 rounded-xl border-2 ${t.is_active ? 'border-[#c9a227] bg-[#faf7ee]/50' : 'border-[hsl(var(--border))] bg-white'}`}>
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 items-end">
                  <Field label="Nombre">
                    <input value={t.name || ''} onChange={e => updateTier(i,'name',e.target.value)} className="w-full px-2 py-1.5 border rounded-md text-sm border-[hsl(var(--border))]" />
                  </Field>
                  <Field label="Precio (cts)" hint={eur(t.amount_cents||0, t.currency||'eur')}>
                    <input type="number" value={t.amount_cents || 0} onChange={e => updateTier(i,'amount_cents',parseInt(e.target.value,10)||0)} className="w-full px-2 py-1.5 border rounded-md text-sm border-[hsl(var(--border))]" />
                  </Field>
                  <Field label="Stripe lookup_key" hint="p.ej. proteccion_lote_zero">
                    <input value={t.stripe_lookup_key || ''} onChange={e => updateTier(i,'stripe_lookup_key',e.target.value)} className="w-full px-2 py-1.5 border rounded-md text-sm font-mono border-[hsl(var(--border))]" />
                  </Field>
                  <Field label="Plazas totales">
                    <input type="number" value={t.seats_total || 0} onChange={e => updateTier(i,'seats_total',parseInt(e.target.value,10)||0)} className="w-full px-2 py-1.5 border rounded-md text-sm border-[hsl(var(--border))]" />
                  </Field>
                  <Field label="Vendidas">
                    <input type="number" value={t.seats_sold || 0} onChange={e => updateTier(i,'seats_sold',parseInt(e.target.value,10)||0)} className="w-full px-2 py-1.5 border rounded-md text-sm border-[hsl(var(--border))]" />
                  </Field>
                  <div className="flex gap-1">
                    {!t.is_active ? (
                      <Button size="sm" variant="ghost" onClick={() => activateTier(t.name)} className="text-xs" data-testid={`pw-activate-${i}`}>
                        Activar
                      </Button>
                    ) : (
                      <span className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#c9a227]/10 text-[#8a7428]">
                        <CheckCircle2 className="w-3 h-3" /> Activo
                      </span>
                    )}
                    <button onClick={() => removeTier(i)} className="p-1.5 rounded-md hover:bg-red-50 text-red-500"><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-[hsl(var(--border))] pt-4 space-y-3">
            <p className="text-sm font-medium">Add-on grabación</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Activo">
                <select value={form.recording_addon_enabled ? 'yes' : 'no'} onChange={e => update('recording_addon_enabled', e.target.value === 'yes')} className="w-full px-3 py-2 border rounded-lg text-sm bg-white border-[hsl(var(--border))]">
                  <option value="yes">Sí (mostrar en checkout)</option>
                  <option value="no">No</option>
                </select>
              </Field>
              <Field label="Precio grabación (cts)" hint={eur(form.recording_addon_price_cents||0)}>
                <input type="number" value={form.recording_addon_price_cents || 0} onChange={e => update('recording_addon_price_cents',parseInt(e.target.value,10)||0)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" />
              </Field>
              <Field label="Stripe lookup_key grabación">
                <input value={form.recording_addon_lookup_key || ''} onChange={e => update('recording_addon_lookup_key',e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm font-mono border-[hsl(var(--border))]" />
              </Field>
            </div>
          </div>
        </div>

        {/* Bloques de texto largo */}
        <div className="card-soft mb-6 space-y-4">
          <h2 className="text-lg font-heading">Secciones (texto)</h2>
          <Field label="Camino — introducción">
            <textarea rows={5} value={form.camino_intro||''} onChange={e=>update('camino_intro',e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" />
          </Field>
          <Field label="Reconoces — intro">
            <textarea rows={3} value={form.reconoces_intro||''} onChange={e=>update('reconoces_intro',e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" />
          </Field>
          <Field label="Qué vas a hacer — intro">
            <textarea rows={3} value={form.what_youll_do_intro||''} onChange={e=>update('what_youll_do_intro',e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" />
          </Field>
          <Field label="Duración (resumen)"><input value={form.duration_summary||''} onChange={e=>update('duration_summary',e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" /></Field>
          <Field label="Por qué barato">
            <textarea rows={5} value={form.why_cheap||''} onChange={e=>update('why_cheap',e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" />
          </Field>
          <Field label="Certificado — texto">
            <textarea rows={3} value={form.certificate_text||''} onChange={e=>update('certificate_text',e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" />
          </Field>
          <Field label="Bio autor">
            <textarea rows={8} value={form.author_bio||''} onChange={e=>update('author_bio',e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" />
          </Field>
          <Field label="Cierre — titular"><input value={form.closing_headline||''} onChange={e=>update('closing_headline',e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" /></Field>
          <Field label="Cierre — cuerpo">
            <textarea rows={4} value={form.closing_body||''} onChange={e=>update('closing_body',e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm border-[hsl(var(--border))]" />
          </Field>
          <Field label="WhatsApp canal (opcional post-compra)">
            <input value={form.whatsapp_channel_url||''} onChange={e=>update('whatsapp_channel_url',e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm font-mono border-[hsl(var(--border))]" placeholder="https://chat.whatsapp.com/..." />
          </Field>
        </div>

        <p className="text-[11px] text-[hsl(var(--text-tertiary))] mb-4">
          Nota: hero bullets, agenda, testimonios, FAQs, "qué vas a hacer", "reconoces" y "lo que te llevas" son listas JSON.
          Se pueden editar en detalle desde la base de datos o abriendo el JSON en Mongo. Todos los cambios de texto principal aquí ya cubren el 90 % del contenido.
        </p>

        <div className="flex justify-end mb-10">
          <Button onClick={save} disabled={saving} className="bg-[#c9a227] hover:bg-[#b89220] text-white" data-testid="pw-admin-save">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>

        {/* Inscritas */}
        <div className="card-soft">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-heading flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#c9a227]"/>Inscritas ({data.enrollments_count})</h2>
              <p className="text-[11px] text-[hsl(var(--text-tertiary))]">Creadas automáticamente tras el pago vía webhook Stripe.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={exportCSV} disabled={!data.enrollments_count}>Exportar CSV</Button>
          </div>
          {data.enrollments_count === 0 ? (
            <p className="text-sm text-[hsl(var(--text-tertiary))] italic">Sin inscripciones todavía.</p>
          ) : (
            <ul className="divide-y divide-[hsl(var(--border))]">
              {data.enrollments.map(e => (
                <li key={e.id || e.stripe_session_id} className="py-3 flex items-center justify-between text-sm gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{e.email}</p>
                    <p className="text-[11px] text-[hsl(var(--text-tertiary))]">{e.tier_name}{e.with_recording ? ' · con grabación' : ''}</p>
                  </div>
                  <span className="text-[11px] text-[hsl(var(--text-tertiary))]">{e.enrolled_at ? new Date(e.enrolled_at).toLocaleString('es-ES') : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
